import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { FiChevronDown, FiCheck } from "react-icons/fi";

/*
 * Jedinstveni custom select za admin panel.
 * - Izgleda kao tekstualni inputi (#141414 pozadina, 1px #333 ivica)
 * - Animiran chevron (rotira 180° pri otvaranju)
 * - Navigacija tastaturom: strelice, Enter/Space, Escape
 * - onChange(value) vraća direktno vrednost (ne event)
 *
 * <CustomSelect
 *   value={statusFilter}
 *   onChange={(v) => setStatusFilter(v)}
 *   options={[{ value: "all", label: "Svi" }, ...]}
 *   placeholder="Izaberi"
 *   fullWidth
 * />
 */

const Wrap = styled.div`
  position: relative;
  display: inline-block;
  width: ${(p) => (p.$fullWidth ? "100%" : "auto")};
  min-width: ${(p) => (p.$fullWidth ? "0" : "170px")};
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 0 0.9rem;
  background: var(--bg-app);
  border: 1px solid ${(p) => (p.$open ? "var(--brand)" : "var(--border-strong)")};
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  white-space: nowrap;
  box-shadow: ${(p) =>
    p.$open ? "0 0 0 3px rgba(255, 107, 53, 0.08)" : "none"};

  &:hover {
    border-color: ${(p) => (p.$open ? "var(--brand)" : "rgba(255,255,255,0.22)")};
  }

  &:focus-visible {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.08);
  }

  svg {
    color: var(--brand);
    flex-shrink: 0;
    transition: transform 0.25s ease;
    transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
  }
`;

const Value = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => (p.$muted ? "#777777" : "inherit")};
`;

/* portal mod: meni ide na document.body (fixed, koordinate iz inline stila)
   da ga ne bi isekao overflow predak, npr. bottom-sheet sa overflow-y: auto */
const Menu = styled.ul`
  position: ${(p) => (p.$portal ? "fixed" : "absolute")};
  z-index: ${(p) => (p.$portal ? 3100 : 60)};
  ${(p) =>
    !p.$portal &&
    css`
      top: ${p.$dropUp ? "auto" : "calc(100% + 6px)"};
      bottom: ${p.$dropUp ? "calc(100% + 6px)" : "auto"};
      left: 0;
      right: 0;
    `}
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--bg-elevated);
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
  max-height: 280px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 107, 53, 0.5);
    border-radius: 999px;
  }
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.7rem;
  border-radius: 6px;
  color: ${(p) => (p.$active ? "var(--brand)" : "#dddddd")};
  background: ${(p) =>
    p.$highlight
      ? "rgba(255, 107, 53, 0.14)"
      : p.$active
        ? "rgba(255, 107, 53, 0.12)"
        : "transparent"};
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease;

  svg {
    color: var(--brand);
    flex-shrink: 0;
  }
`;

const CustomSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Izaberi",
  fullWidth = false,
  dropUp = false,
  portal = false,
  id,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [menuPos, setMenuPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  // Pozicija portalovanog menija, računa se iz pozicije trigger-a na ekranu
  const updateMenuPos = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setMenuPos({
      left: rect.left,
      width: rect.width,
      ...(dropUp
        ? {
            bottom: window.innerHeight - rect.top + 6,
            maxHeight: Math.min(280, rect.top - 22),
          }
        : {
            top: rect.bottom + 6,
            maxHeight: Math.min(280, window.innerHeight - rect.bottom - 22),
          }),
    });
  }, [dropUp]);

  const openMenu = () => {
    const idx = options.findIndex((o) => String(o.value) === String(value));
    setHighlight(idx >= 0 ? idx : 0);
    if (portal) updateMenuPos();
    setOpen(true);
  };

  const selectOption = useCallback(
    (option) => {
      onChange(option.value);
      setOpen(false);
    },
    [onChange],
  );

  const handleDocClick = useCallback((e) => {
    if (
      ref.current &&
      !ref.current.contains(e.target) &&
      !(menuRef.current && menuRef.current.contains(e.target))
    ) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("mousedown", handleDocClick);

    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Tab") {
        setOpen(false);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, options.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (highlight >= 0 && options[highlight]) {
          selectOption(options[highlight]);
        }
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, highlight, options, selectOption, handleDocClick]);

  // Drži istaknutu opciju u vidljivom delu menija pri kretanju strelicama
  useEffect(() => {
    if (!open || highlight < 0 || !menuRef.current) return;
    const el = menuRef.current.children[highlight];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  // Portalovan meni prati trigger pri skrolu (i unutar sheeta) i resize-u
  useEffect(() => {
    if (!open || !portal) return undefined;
    window.addEventListener("resize", updateMenuPos);
    window.addEventListener("scroll", updateMenuPos, true);
    return () => {
      window.removeEventListener("resize", updateMenuPos);
      window.removeEventListener("scroll", updateMenuPos, true);
    };
  }, [open, portal, updateMenuPos]);

  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <Wrap ref={ref} $fullWidth={fullWidth} className={className}>
      <Trigger
        type="button"
        id={id}
        $open={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            openMenu();
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Value $muted={!selected}>
          {selected ? selected.label : placeholder}
        </Value>
        <FiChevronDown size={18} />
      </Trigger>

      {open &&
        (() => {
          const menu = (
            <Menu
              role="listbox"
              $dropUp={dropUp}
              $portal={portal}
              ref={menuRef}
              style={portal && menuPos ? menuPos : undefined}
            >
              {options.map((o, i) => {
                const active = String(o.value) === String(value);
                return (
                  <Item
                    key={String(o.value)}
                    role="option"
                    aria-selected={active}
                    $active={active}
                    $highlight={i === highlight}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => selectOption(o)}
                  >
                    <span>{o.label}</span>
                    {active && <FiCheck size={15} />}
                  </Item>
                );
              })}
            </Menu>
          );
          return portal ? createPortal(menu, document.body) : menu;
        })()}
    </Wrap>
  );
};

export default CustomSelect;
